/**
 * Live store-wide review count and rating.
 *
 * The numbers used to be typed into section settings and went stale - the
 * store read "650 reviews" at 694. Okendo publishes the totals at
 * `review_aggregate`, the same endpoint its own reviews page calls, so the
 * figures Liquid rendered get corrected here.
 *
 * Elements to fill are marked `data-ag-okendo-stat="count|rating"` by the
 * `ag-okendo-stats` snippet, which also renders the fallback values, so a
 * failed fetch simply leaves the page as Liquid rendered it.
 */

const CACHE_KEY = 'ag-okendo-aggregate';
const CACHE_MS = 60 * 60 * 1000;

function subscriberId() {
  try {
    return JSON.parse(document.querySelector('#oke-reviews-settings').textContent).subscriberId;
  } catch {
    return null;
  }
}

function cached() {
  try {
    const { at, data } = JSON.parse(sessionStorage.getItem(CACHE_KEY));
    return Date.now() - at < CACHE_MS ? data : null;
  } catch {
    return null;
  }
}

function cache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    // Private browsing, or storage full. The numbers still render.
  }
}

async function aggregate() {
  const hit = cached();
  if (hit) return hit;

  const store = subscriberId();
  if (!store) return null;

  try {
    const res = await fetch(`https://api.okendo.io/v1/stores/${store}/review_aggregate`);
    if (!res.ok) return null;
    const { reviewAggregate } = await res.json();
    if (!reviewAggregate?.ratingAndReviewCount) return null;

    // Okendo reports the total of every star value rather than an average.
    const count = reviewAggregate.ratingAndReviewCount;
    const data = {
      count,
      rating: Math.round((reviewAggregate.ratingAndReviewValuesTotal / count) * 10) / 10,
    };
    cache(data);
    return data;
  } catch {
    return null;
  }
}

const targets = document.querySelectorAll('[data-ag-okendo-stat]');
if (targets.length) {
  const data = await aggregate();
  if (data) {
    for (const el of targets) {
      const value = el.dataset.agOkendoStat === 'rating' ? data.rating : data.count.toLocaleString();
      // Leave the DOM alone when nothing changed, so no needless repaint.
      if (el.textContent !== String(value)) el.textContent = value;
    }
  }
}
